/**
 * `P0-UIK-013`: the route table and its guards, as DATA and a pure function.
 *
 * **Why there is no router library here.** Canon fixes the route paths of all
 * six apps (4.6.4) and the rule that governs them, and it names TanStack Query
 * and Zustand by name (4.6.5) , but it never names a router. Picking one in a
 * shared package would commit all three apps to it on my authority, and the
 * ruling that minted this package asked for *"the router factory and its route
 * guards"*, not for a dependency. So the guard is a pure function over a table:
 * the apps bind it to whatever renders their outlet, and the decision that
 * matters , WHO may see WHAT , is testable with no DOM, no history and no
 * library at all.
 *
 * **The rule this enforces**, 4.6.4 verbatim for the console and true of every
 * app: *"The nav rail renders only the routes in the role's screen whitelist.
 * A direct URL outside the whitelist redirects to the role's landing screen."*
 *
 * **Danger sentence.** The tempting shape is to hide forbidden entries from
 * the nav and stop there. That is not a guard, it is a decoration: the URL is
 * typed, pasted, bookmarked and shared, and a screen reachable by typing its
 * path is reachable. Worse, hiding-only fails INVISIBLY , the nav looks
 * correct in every review. So the whitelist is consulted on RESOLUTION, and
 * `navigation()` reads the same list, which is what keeps the two answers from
 * drifting apart.
 *
 * This is the client-side half of a two-sided rule and never the whole of it.
 * 6.1.8's four layers run on the server and are what actually refuses; a
 * redirect here spares the user a screen that would only have shown them a
 * `403`.
 */

export interface RouteDefinition {
  /** The path pattern, `:name` marking a segment capture, exactly as 4.6.4 writes it. */
  readonly path: string;
  /** The stable identifier a whitelist names. Never the path: paths change. */
  readonly screen: string;
  /**
   * A route served without a session: the client's `/depot/:token` and the
   * cabinet tour link (4.6.4, 6.3.6). The token IS the authorisation, so
   * `AuthGate` must not send its holder to a login they cannot pass.
   */
  readonly public?: boolean;
}

export interface RouteTable {
  readonly routes: readonly RouteDefinition[];
  /** Where a refused or unknown path sends a caller. Must itself be whitelisted. */
  readonly landingScreen: string;
}

export type RouteResolution =
  | {
      readonly kind: "render";
      readonly route: RouteDefinition;
      readonly params: Readonly<Record<string, string>>;
    }
  | { readonly kind: "redirect"; readonly to: string; readonly reason: "not_whitelisted" | "unknown_path" };

const SEGMENTS = (path: string): readonly string[] => path.split("/").filter((part) => part !== "");

/** `/dossiers/:id` against `/dossiers/42`, and the captures it yields. */
function match(pattern: string, path: string): Record<string, string> | null {
  const expected = SEGMENTS(pattern);
  const actual = SEGMENTS(path);
  if (expected.length !== actual.length) return null;

  const params: Record<string, string> = {};
  for (const [index, part] of expected.entries()) {
    // eslint-disable-next-line security/detect-object-injection -- `index` is the loop's own counter over the same array.
    const given = actual[index] ?? "";
    if (part.startsWith(":")) {
      // An empty capture is not a match: `/vehicule/` must not resolve as a
      // vehicle whose id is the empty string, which would then be sent to the
      // API as one.
      if (given === "") return null;
      params[part.slice(1)] = decodeURIComponent(given);
      continue;
    }
    if (part !== given) return null;
  }
  return params;
}

/** The route a path names, whitelist ignored. Exported for the nav, not for the guard. */
export function find(
  table: RouteTable,
  path: string,
): { route: RouteDefinition; params: Record<string, string> } | null {
  for (const route of table.routes) {
    const params = match(route.path, path);
    if (params !== null) return { route, params };
  }
  return null;
}

/**
 * Whether a path is served WITHOUT a session, asked of the path alone.
 *
 * `P0-UIK-024`. `resolveRoute` needs a whitelist, and a whitelist comes from
 * the role, and the role comes from `/me` , so an application cannot use it to
 * decide whether to CALL `/me` in the first place. That circle is how
 * dealer-web came to boot its session on a public route and revive another
 * application's session through a shared refresh cookie.
 *
 * A public route's authorisation is its token, and whether a route is public is
 * a property of the table, not of the caller. So this question has an answer
 * before anybody knows who is asking.
 */
export function isPublicPath(table: RouteTable, path: string): boolean {
  return find(table, path)?.route.public === true;
}

/**
 * The guard. Given a path and the role's whitelist, either render or redirect.
 *
 * `whitelist` is a set of SCREEN ids , 6.1.7's role screens , and a public
 * route bypasses it because its token is its authorisation.
 */
export function resolveRoute(
  table: RouteTable,
  path: string,
  whitelist: ReadonlySet<string>,
): RouteResolution {
  const landing = table.routes.find((route) => route.screen === table.landingScreen);
  if (landing === undefined) {
    // A table whose landing screen is not in it sends every refusal to a path
    // that does not exist, which turns one refused URL into an infinite
    // redirect. Refused loudly at the only moment a developer sees it.
    throw new Error(`route table's landing screen "${table.landingScreen}" is not one of its routes`);
  }

  const found = find(table, path);
  if (found === null) return { kind: "redirect", to: landing.path, reason: "unknown_path" };
  if (found.route.public === true) return { kind: "render", route: found.route, params: found.params };
  if (!whitelist.has(found.route.screen)) {
    return { kind: "redirect", to: landing.path, reason: "not_whitelisted" };
  }
  return { kind: "render", route: found.route, params: found.params };
}

/**
 * The routes a nav may show. The SAME whitelist the guard consults, which is
 * the point: a nav computed from a second list is a nav that drifts, and the
 * drift shows as either a dead entry or a hidden-but-reachable screen.
 *
 * **One entry per SCREEN, not per route**, and the founder's sitting of 27
 * August 2026 is why. This returned every whitelisted route, so the console's
 * rail showed *Dossiers*, *Partenaires* and *Expertises* TWICE , once for the
 * list route and once for its detail route , while the profile screen's own
 * list of the same view held eleven, deduplicated. Two screens on one page
 * disagreeing about what a role may see is precisely the drift this function
 * exists to prevent, and it was doing it itself.
 *
 * A detail route belongs to its parent screen and is not a destination a rail
 * offers: nobody navigates to `/dossiers/:id` without an id. The FIRST route of
 * each screen wins, which is the list route , the table is written parent-first
 * for exactly this reason, and `views.spec.ts` asserts that it stays so.
 */
export function navigation(table: RouteTable, whitelist: ReadonlySet<string>): readonly RouteDefinition[] {
  const seen = new Set<string>();
  return table.routes.filter((route) => {
    if (route.public === true || !whitelist.has(route.screen) || seen.has(route.screen)) return false;
    seen.add(route.screen);
    return true;
  });
}
